import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { DateTimeDisplay, ActionList, Breadcrumbs, StatusMessage, SearchQuery, Sorting } from "../../components/ui";
import { FilterBtn } from "../../components/ui/Buttons";
import SortSummary from "../../components/ui/SortSummary";
import RuleStatusFilter, { type RuleEntityTypeValue } from "../../components/filters/RuleStatusFilter";
import { useBreadcrumbs } from "../../hooks/useBreadcrumbs";
import { patchRuleStatus, deleteRule, getRule, updateRule, createRule, listAllRules, listSiteRules } from "../../services/ruleManagement/ruleManagement.service";
import {
    addVirtualTenantAlarmPaths,
    getSite,
    getVirtualTenantAlarmPaths,
    listVirtualTenants,
    getVirtualTenantsBySite,
} from "../../services/siteManagement/siteManagement.service";
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import type { RuleSummary } from "../../services/ruleManagement/ruleManagement.types";
import type { SiteListItem } from "../../services/siteManagement/siteManagement.types";
import { formatDateTime } from "../../helpers/formatDateTime";
import Modal from "../../components/ui/Modal";
import Pagination from "../../components/ui/Pagination";
import RulesForm, { type RuleFormData } from "../../components/forms/RulesForm";
import { ModalContent } from "../../components/ui/ModalContent";

interface RuleWithContext extends RuleSummary {
    customerName?: string;
    customerId?: number;
    siteId?: number;
}

const SORT_COLUMN_LABELS: Record<string, string> = {
    ruleName: 'Rule Name',
    siteName: 'Site / VT',
    customerName: 'Customer',
    status: 'Status',
    distributionGroups: 'Distribution Groups',
    attachmentGroups: 'Attachment Groups',
    createdAt: 'Created',
};

const RulesDetails: React.FC = () => {
    const navigate = useNavigate();
    const breadcrumbs = useBreadcrumbs({
        currentPageTitle: 'Rules Details',
    });

    const [rules, setRules] = useState<RuleWithContext[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [sitesMap, setSitesMap] = useState<Map<number, SiteListItem>>(new Map());
    const [virtualTenantsMap, setVirtualTenantsMap] = useState<Map<number, string>>(new Map());
    const [virtualTenantsByParentSite, setVirtualTenantsByParentSite] = useState<Map<number, Array<{ id: number; name: string }>>>(new Map());
    const [virtualTenantsByParentSiteName, setVirtualTenantsByParentSiteName] = useState<Map<string, Array<{ id: number; name: string }>>>(new Map());
    const [ruleToVirtualTenantNames, setRuleToVirtualTenantNames] = useState<Map<number, string[]>>(new Map());
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [entityTypeFilter, setEntityTypeFilter] = useState<RuleEntityTypeValue>('all');
    const [sortColumn, setSortColumn] = useState<string>('');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
    const [editingRuleData, setEditingRuleData] = useState<RuleFormData | null>(null);
    const [editingSiteId, setEditingSiteId] = useState<number | null>(null);
    const [editingRuleIds, setEditingRuleIds] = useState<number[]>([]);
    const [editingSiteIds, setEditingSiteIds] = useState<number[]>([]);
    const [isEditFormValid, setIsEditFormValid] = useState<boolean>(true);
    const [showEditErrorSummary, setShowEditErrorSummary] = useState<boolean>(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
    const [newRulesData, setNewRulesData] = useState<RuleFormData[]>([]);
    const [isCreateFormValid, setIsCreateFormValid] = useState<boolean>(true);
    const [showCreateErrorSummary, setShowCreateErrorSummary] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const pageSize = 50;
    
    // Modal confirmation for delete
    const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
    const [modalHeadingText, setModalHeadingText] = useState<string | undefined>(undefined);
    const [modalMessageText, setModalMessageText] = useState<string | undefined>(undefined);
    const [pendingDeleteRuleIds, setPendingDeleteRuleIds] = useState<number[]>([]);
    const resolverRef = useRef<((value: boolean) => void) | null>(null);
    
    // Track rule ID -> correct site registry ID mapping for newly created rules
    // This is needed because backend rules_list returns wrong siteHierarchyId
    // Persist mapping across page reloads so newly-created rules keep their correct site
    const loadMappingFromStorage = () => {
        try {
            const raw = localStorage.getItem('rules:ruleToSiteMapping');
            if (!raw) {
                return new Map<number, number>();
            }
            const arr = JSON.parse(raw) as Array<[number, number]>;
            return new Map<number, number>(arr.map(([k, v]) => [Number(k), Number(v)]));
        } catch (e) {
            return new Map<number, number>();
        }
    };

    const [ruleToSiteMapping, setRuleToSiteMapping] = useState<Map<number, number>>(loadMappingFromStorage);

    const loadVirtualTenantMappingFromStorage = () => {
        try {
            const raw = localStorage.getItem('rules:ruleToVirtualTenantMapping');
            if (!raw) {
                return new Map<number, string[]>();
            }
            const arr = JSON.parse(raw) as Array<[number, string[]]>;
            return new Map<number, string[]>(arr.map(([k, v]) => [Number(k), Array.isArray(v) ? v.map(String) : []]));
        } catch (_e) {
            return new Map<number, string[]>();
        }
    };

    const [ruleToVirtualTenantMapping, setRuleToVirtualTenantMapping] = useState<Map<number, string[]>>(loadVirtualTenantMappingFromStorage);

    const getVirtualTenantIdsForRule = (rule: RuleWithContext): number[] => {
        const mapped = (ruleToVirtualTenantMapping.get(rule.siteRuleId) || [])
            .map(id => Number(id))
            .filter(id => !Number.isNaN(id) && id > 0);

        // Fallback: some payloads expose VT via siteHierarchyId even when explicit mapping is missing.
        const hierarchyAsVtId = Number(rule.siteHierarchyId);
        if (!Number.isNaN(hierarchyAsVtId) && hierarchyAsVtId > 0 && virtualTenantsMap.has(hierarchyAsVtId)) {
            mapped.push(hierarchyAsVtId);
        }

        return mapped.filter((id, index, arr) => arr.indexOf(id) === index);
    };

    const getRuleSiteOrVirtualTenantLabel = (rule: RuleWithContext): string => {
        const mappedVtIds = getVirtualTenantIdsForRule(rule);

        if (mappedVtIds.length > 0) {
            const firstVtId = mappedVtIds[0];
            return virtualTenantsMap.get(firstVtId) || `VT ${firstVtId}`;
        }

        const inferredVtNames = ruleToVirtualTenantNames.get(rule.siteRuleId) || [];
        if (inferredVtNames.length > 0) {
            return inferredVtNames[0] || '';
        }

        const looksLikeVtRule = /(^|\s)vt(\s|$)/i.test(String(rule.ruleName || ''));
        if (looksLikeVtRule) {
            const parentCandidates = [rule.siteId, rule.siteHierarchyId]
                .map(id => Number(id))
                .filter(id => !Number.isNaN(id) && id > 0);

            for (const parentId of parentCandidates) {
                const vtList = virtualTenantsByParentSite.get(parentId) || [];
                if (vtList.length > 0) {
                    return vtList[0].name;
                }
            }

            const normalizedSiteName = String(rule.siteName || '').trim().toLowerCase();
            if (normalizedSiteName) {
                const byName = virtualTenantsByParentSiteName.get(normalizedSiteName) || [];
                if (byName.length > 0) {
                    return byName[0].name;
                }
            }
        }

        return rule.siteName || rule.siteIdentifier || '';
    };

    const parseFilteringPaths = (val?: string): number[] => {
        if (!val) {
            return [];
        }
        return val.toString().split(',')
            .map(s => Number(s.trim()))
            .filter(n => !Number.isNaN(n) && Number(n) > 0);
    };

    const extractRuleAlarmPathLabelsById = (alarmPaths: unknown): Record<string, string> => {
        if (!Array.isArray(alarmPaths)) {
            return {};
        }

        const labelsById: Record<string, string> = {};
        alarmPaths.forEach((item: unknown) => {
            if (typeof item === 'object' && item !== null) {
                const obj = item as Record<string, unknown>;
                const maybeId = obj.id ?? obj.alarmPathId;
                const numericId = Number(maybeId);
                const pathToAlarm = typeof obj.pathToAlarm === 'string' ? obj.pathToAlarm : '';
                if (!Number.isNaN(numericId) && numericId > 0 && pathToAlarm) {
                    labelsById[String(numericId)] = pathToAlarm;
                }
            }
        });

        return labelsById;
    };

    const extractAlarmPathIdsFromVirtualTenantPayload = (data: unknown): number[] => {
        if (!Array.isArray(data)) {
            return [];
        }

        const ids: number[] = [];
        data.forEach((item: unknown) => {
            if (typeof item === 'number' && item > 0) {
                ids.push(item);
                return;
            }

            if (typeof item === 'object' && item !== null) {
                const obj = item as Record<string, unknown>;
                const maybeId = obj.alarmPathId ?? obj.id;
                if (typeof maybeId === 'number' && maybeId > 0) {
                    ids.push(maybeId);
                }
            }
        });

        return ids;
    };

    useEffect(() => {
        try {
            const arr = Array.from(ruleToSiteMapping.entries());
            localStorage.setItem('rules:ruleToSiteMapping', JSON.stringify(arr));
        } catch (e) {
            console.info('[Rules] Failed to persist ruleToSiteMapping', e);
        }
    }, [ruleToSiteMapping]);

    useEffect(() => {
        try {
            const arr = Array.from(ruleToVirtualTenantMapping.entries());
            localStorage.setItem('rules:ruleToVirtualTenantMapping', JSON.stringify(arr));
        } catch (e) {
            console.info('[Rules] Failed to persist ruleToVirtualTenantMapping', e);
        }
    }, [ruleToVirtualTenantMapping]);

    const fetchRules = async () => {
        setLoading(true);
        setError(null);

        try {
            const res = await listAllRules();
            if (!res.success) {
                setError(res.error?.message || "Failed to load rules");
                setLoading(false);
                return;
            }

            const items = res.data;
            if (!Array.isArray(items)) {
                setError("Failed to load rules: unexpected response format");
                setLoading(false);
                return;
            }

            // First pass: map rules with whatever data we have from RuleManagement
            const initialMappedRules: RuleWithContext[] = items.map((item: any) => ({
                ...item,
                // CRITICAL: Use siteIdentifierRegistryId if available (canonical registry ID)
                // Fall back to siteHierarchyId only if registry ID is missing
                // Note: siteHierarchyId can point to wrong site if backend data is stale!
                siteId: (item.siteIdentifierRegistryId ?? item.siteHierarchyId) ? Number(item.siteIdentifierRegistryId ?? item.siteHierarchyId) : undefined,
                // Capture customer info if backend provides it
                customerId: item.customerId ? Number(item.customerId) : undefined,
                customerName: item.customerName || undefined,
            }));

            // Get unique site IDs and fetch site data in batches to enrich with correct info
            const uniqueSiteIds = new Set<number>();
            initialMappedRules.forEach(rule => {
                if (rule.siteId) {
                    uniqueSiteIds.add(rule.siteId);
                }
            });

            // Fetch all unique sites in parallel (this is much faster than sequential)
            const siteDataMap = new Map<number, any>();
            const siteFetchPromises = Array.from(uniqueSiteIds).map(async siteId => {
                try {
                    const siteRes = await getSite(siteId);
                    if (siteRes?.success && siteRes.data) {
                        siteDataMap.set(siteId, siteRes.data);
                    }
                } catch (err) {
                    console.info(`[Rules] Could not fetch site ${siteId}:`, err);
                }
            });

            await Promise.all(siteFetchPromises);

            // Enrich rules with the fetched site data
            const enrichedRules: RuleWithContext[] = initialMappedRules.map(rule => {
                if (!rule.siteId) {
                    return rule;
                }

                const siteData = siteDataMap.get(rule.siteId);
                if (siteData) {
                    const registryIdFromSiteData = (siteData as any).siteIdentifierRegistryId ?? (siteData as any).siteRegistryId ?? (siteData.id ?? undefined);
                    const finalSiteId = rule.siteId || (registryIdFromSiteData ? Number(registryIdFromSiteData) : undefined);

                    // Log when enrichment would change the siteId
                    if (finalSiteId !== rule.siteId) {
                        console.info(`[Rules] Enrichment replacing missing siteId for rule ${rule.siteRuleId}: using ${finalSiteId}`);
                    }

                    return {
                        ...rule,
                        siteName: siteData.siteName ?? siteData.name,
                        siteIdentifier: siteData.siteIdentifier,
                        customerName: siteData.customerName,
                        customerId: siteData.customerId,
                        siteId: finalSiteId
                    };
                }

                // If no site data found, keep the original rule data
                return rule;
            });

            const missingTrackedSites = new Set<number>();
            Array.from(ruleToSiteMapping.values()).forEach(siteId => {
                if (!siteDataMap.has(siteId)) {
                    missingTrackedSites.add(siteId);
                }
            });
            
            if (missingTrackedSites.size > 0) {
                const missingSiteFetches = Array.from(missingTrackedSites).map(async siteId => {
                    try {
                        const siteRes = await getSite(siteId);
                        if (siteRes?.success && siteRes.data) {
                            siteDataMap.set(siteId, siteRes.data);
                        }
                    } catch (err) {
                        console.info(`[Rules] Could not fetch tracked site ${siteId}:`, err);
                    }
                });
                await Promise.all(missingSiteFetches);
            }
            
            const finalRules: RuleWithContext[] = enrichedRules.map(rule => {
                const correctSiteId = ruleToSiteMapping.get(rule.siteRuleId);
                if (correctSiteId) {
                    
                    const correctSiteData = siteDataMap.get(correctSiteId);
                    if (correctSiteData) {
                        return {
                            ...rule,
                            siteId: correctSiteId,
                            siteName: correctSiteData.siteName ?? correctSiteData.name,
                            siteIdentifier: correctSiteData.siteIdentifier,
                            customerName: correctSiteData.customerName,
                            customerId: correctSiteData.customerId,
                        };
                    } else {
                        console.info(`[Rules] No site data found for tracked site ${correctSiteId}, keeping original rule data`);
                    }
                }
                return rule;
            });

            const siteMap = new Map<number, SiteListItem>();
            finalRules.forEach(rule => {
                if (rule.siteId) {
                    siteMap.set(rule.siteId, {
                        id: rule.siteId,
                        siteName: rule.siteName,
                        siteIdentifier: rule.siteIdentifier,
                        siteHierarchyId: rule.siteHierarchyId,
                        customerName: rule.customerName,
                        customerId: rule.customerId,
                    } as SiteListItem);
                }
            });

            // Infer VT labels and IDs for existing rules so Site/VT column shows VT names for VT-applied rules.
            const inferredNamesByRule = new Map<number, string[]>();
            const inferredVtIdsByRule = new Map<number, string[]>();
            const addUniqueString = (map: Map<number, string[]>, key: number, value: string) => {
                if (!value) {
                    return;
                }
                const trimmed = value.trim();
                if (!trimmed) {
                    return;
                }
                const existing = map.get(key) || [];
                if (!existing.includes(trimmed)) {
                    map.set(key, [...existing, trimmed]);
                }
            };

            const inferVtPromises = Array.from(uniqueSiteIds).map(async (siteId) => {
                try {
                    const siteRuleLookupIds = [siteId]
                        .filter((id, index, arr) => arr.indexOf(id) === index);

                    let siteRulesRes: any;
                    for (const lookupId of siteRuleLookupIds) {
                        try {
                            const candidate = await listSiteRules(lookupId);
                            if (candidate?.success) {
                                siteRulesRes = candidate;
                                if (Array.isArray(candidate.data?.virtualTenants) && candidate.data.virtualTenants.length > 0) {
                                    break;
                                }
                            }
                        } catch {
                            // ignore and try next id
                        }
                    }

                    if (!siteRulesRes?.success || !Array.isArray(siteRulesRes.data?.virtualTenants) || siteRulesRes.data.virtualTenants.length === 0) {
                        return;
                    }

                    const lookupIds = [siteRulesRes.data.siteHierarchyId, siteId]
                        .map(id => Number(id))
                        .filter(id => !Number.isNaN(id) && id > 0)
                        .filter((id, index, arr) => arr.indexOf(id) === index);

                    let bySiteVts: Array<{ id?: number; name?: string; virtualTenantIdentifier?: string }> = [];
                    for (const lookupId of lookupIds) {
                        try {
                            const vtRes = await getVirtualTenantsBySite(lookupId);
                            if (vtRes?.success && Array.isArray(vtRes.data) && vtRes.data.length > 0) {
                                bySiteVts = vtRes.data;
                                break;
                            }
                        } catch {
                            // ignore
                        }
                    }

                    siteRulesRes.data.virtualTenants.forEach((vtContainer: any) => {
                        const vtName = String(vtContainer?.name || '').trim();
                        const normalizedName = vtName.toLowerCase();

                        let resolvedVtId: number | undefined;
                        if (bySiteVts.length > 0 && normalizedName) {
                            const matched = bySiteVts.find(vt => {
                                const candidateName = String(vt?.name || vt?.virtualTenantIdentifier || '').trim().toLowerCase();
                                return candidateName && candidateName === normalizedName;
                            });
                            if (matched?.id) {
                                const numericId = Number(matched.id);
                                if (!Number.isNaN(numericId) && numericId > 0) {
                                    resolvedVtId = numericId;
                                }
                            }
                        }

                        if (!resolvedVtId) {
                            const fallbackIds = [vtContainer?.id, vtContainer?.virtualTenantId, vtContainer?.siteHierarchyId, vtContainer?.siteIdentifierRegistryId]
                                .map((id: unknown) => Number(id))
                                .filter((id: number) => !Number.isNaN(id) && id > 0);
                            if (fallbackIds.length > 0) {
                                resolvedVtId = fallbackIds[0];
                            }
                        }

                        if (Array.isArray(vtContainer?.rules)) {
                            vtContainer.rules.forEach((vtRule: any) => {
                                const ruleId = Number(vtRule?.siteRuleId ?? vtRule?.id ?? vtRule?.ruleId);
                                if (Number.isNaN(ruleId) || ruleId <= 0) {
                                    return;
                                }

                                if (vtName) {
                                    addUniqueString(inferredNamesByRule, ruleId, vtName);
                                }

                                if (resolvedVtId) {
                                    addUniqueString(inferredVtIdsByRule, ruleId, String(resolvedVtId));
                                }
                            });
                        }
                    });
                } catch {
                    // ignore per-site VT inference failures
                }
            });

            await Promise.all(inferVtPromises);

            // Per-rule fallback: directly resolve VT name for rules still missing VT inference.
            const unresolvedRules = finalRules.filter(rule => !inferredNamesByRule.has(rule.siteRuleId));
            const resolveSingleRuleVtPromises = unresolvedRules.map(async (rule) => {
                const lookupIds = [
                    rule.siteId,
                    rule.siteIdentifierRegistryId,
                    ruleToSiteMapping.get(rule.siteRuleId),
                ]
                    .map(id => Number(id))
                    .filter(id => !Number.isNaN(id) && id > 0)
                    .filter((id, index, arr) => arr.indexOf(id) === index);

                if (lookupIds.length === 0) {
                    return;
                }

                for (const lookupId of lookupIds) {
                    try {
                        const siteRulesRes = await listSiteRules(lookupId);
                        if (siteRulesRes?.success && Array.isArray(siteRulesRes.data?.virtualTenants) && siteRulesRes.data.virtualTenants.length > 0) {
                            const matchingVt = siteRulesRes.data.virtualTenants.find((vtContainer: any) => {
                                if (!Array.isArray(vtContainer?.rules)) {
                                    return false;
                                }
                                return vtContainer.rules.some((vtRule: any) => {
                                    const candidateRuleId = Number(vtRule?.siteRuleId ?? vtRule?.id ?? vtRule?.ruleId);
                                    if (!Number.isNaN(candidateRuleId) && candidateRuleId === rule.siteRuleId) {
                                        return true;
                                    }

                                    const candidateRuleName = String(vtRule?.ruleName || '').trim().toLowerCase();
                                    const currentRuleName = String(rule.ruleName || '').trim().toLowerCase();
                                    return !!candidateRuleName && !!currentRuleName && candidateRuleName === currentRuleName;
                                });
                            });

                            if (matchingVt) {
                                const vtName = String(matchingVt?.name || '').trim();
                                if (vtName) {
                                    addUniqueString(inferredNamesByRule, rule.siteRuleId, vtName);
                                }

                                const vtIdCandidates = [matchingVt?.siteHierarchyId]
                                    .map((id: unknown) => Number(id))
                                    .filter((id: number) => !Number.isNaN(id) && id > 0);
                                if (vtIdCandidates.length > 0) {
                                    addUniqueString(inferredVtIdsByRule, rule.siteRuleId, String(vtIdCandidates[0]));
                                }
                                break;
                            }
                        }

                        // Last-resort fallback: match by alarm-path subset against VTs under this site.
                        const ruleDetailRes = await getRule(rule.siteRuleId);
                        const rulePathIds = (ruleDetailRes?.success && ruleDetailRes.data && Array.isArray(ruleDetailRes.data.alarmPaths))
                            ? ruleDetailRes.data.alarmPaths
                                .map(p => Number(p.id))
                                .filter(id => !Number.isNaN(id) && id > 0)
                            : [];

                        if (rulePathIds.length === 0) {
                            continue;
                        }

                        const rulePathSet = new Set<number>(rulePathIds);
                        const vtListRes = await getVirtualTenantsBySite(lookupId);
                        if (!vtListRes?.success || !Array.isArray(vtListRes.data) || vtListRes.data.length === 0) {
                            continue;
                        }

                        let matchedByPath = false;
                        for (const vt of vtListRes.data) {
                            const vtId = Number(vt.id);
                            if (Number.isNaN(vtId) || vtId <= 0) {
                                continue;
                            }

                            let vtPathSet = new Set<number>();
                            try {
                                const vtPathsRes = await getVirtualTenantAlarmPaths(vtId);
                                if (vtPathsRes?.success && Array.isArray(vtPathsRes.data)) {
                                    const ids = extractAlarmPathIdsFromVirtualTenantPayload(vtPathsRes.data);
                                    vtPathSet = new Set(ids);
                                }
                            } catch {
                                // ignore per-vt path lookup errors
                            }

                            if (vtPathSet.size === 0 && Array.isArray(vt.associatedAlarmPaths)) {
                                vtPathSet = new Set(
                                    vt.associatedAlarmPaths
                                        .map(id => Number(id))
                                        .filter(id => !Number.isNaN(id) && id > 0)
                                );
                            }

                            if (vtPathSet.size === 0 || rulePathSet.size > vtPathSet.size) {
                                continue;
                            }

                            let allPresent = true;
                            for (const pathId of rulePathSet) {
                                if (!vtPathSet.has(pathId)) {
                                    allPresent = false;
                                    break;
                                }
                            }

                            if (allPresent) {
                                const vtName = String(vt.name || vt.virtualTenantIdentifier || '').trim();
                                if (vtName) {
                                    addUniqueString(inferredNamesByRule, rule.siteRuleId, vtName);
                                }
                                addUniqueString(inferredVtIdsByRule, rule.siteRuleId, String(vtId));
                                matchedByPath = true;
                                break;
                            }
                        }

                        if (matchedByPath) {
                            break;
                        }
                    } catch {
                        // ignore per-lookup failures and keep trying
                    }
                }
            });

            await Promise.all(resolveSingleRuleVtPromises);

            if (inferredNamesByRule.size > 0) {
                setRuleToVirtualTenantNames(inferredNamesByRule);
            }
            if (inferredVtIdsByRule.size > 0) {
                setRuleToVirtualTenantMapping(prev => {
                    const merged = new Map(prev);
                    inferredVtIdsByRule.forEach((ids, ruleId) => {
                        const existing = merged.get(ruleId) || [];
                        const combined = [...new Set([...existing.map(String), ...ids.map(String)])];
                        if (combined.length > 0) {
                            merged.set(ruleId, combined);
                        }
                    });
                    return merged;
                });
            }
            
            setRules(finalRules);
            setSitesMap(siteMap);
            setLoading(false);
        } catch (err: any) {
            setError(err?.message || "Failed to load rules");
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchVirtualTenants = async () => {
            try {
                const vtRes = await listVirtualTenants();
                if (vtRes?.success && Array.isArray(vtRes.data)) {
                    const vtMap = new Map<number, string>();
                    const byParent = new Map<number, Array<{ id: number; name: string }>>();
                    const byParentName = new Map<string, Array<{ id: number; name: string }>>();
                    vtRes.data.forEach(vt => {
                        const vtId = Number(vt.id);
                        if (!Number.isNaN(vtId) && vtId > 0) {
                            const vtName = vt.name || vt.virtualTenantIdentifier || `VT ${vtId}`;
                            vtMap.set(vtId, vtName);

                            const parentCandidates = [vt.parentId, vt.parentSiteHierarchyId, vt.siteIdentifierRegistryId]
                                .map(id => Number(id))
                                .filter(id => !Number.isNaN(id) && id > 0)
                                .filter((id, index, arr) => arr.indexOf(id) === index);

                            parentCandidates.forEach(parentId => {
                                const existing = byParent.get(parentId) || [];
                                if (!existing.some(item => item.id === vtId)) {
                                    byParent.set(parentId, [...existing, { id: vtId, name: vtName }]);
                                }
                            });

                            const normalizedParentName = String(vt.parentName || '').trim().toLowerCase();
                            if (normalizedParentName) {
                                const existingByName = byParentName.get(normalizedParentName) || [];
                                if (!existingByName.some(item => item.id === vtId)) {
                                    byParentName.set(normalizedParentName, [...existingByName, { id: vtId, name: vtName }]);
                                }
                            }
                        }
                    });
                    setVirtualTenantsMap(vtMap);
                    setVirtualTenantsByParentSite(byParent);
                    setVirtualTenantsByParentSiteName(byParentName);
                }
            } catch (e) {
                console.info('[Rules] Failed to load virtual tenants for table links', e);
            }
        };

        void fetchRules();
        void fetchVirtualTenants();
    }, []);

    const handleSort = (column: string) => {
        setCurrentPage(1);
        if (sortColumn === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortOrder('asc');
        }
    };

    const handleResetSort = () => {
        setSortColumn('');
        setSortOrder('asc');
        setCurrentPage(1);
    };

    const handleActivateRule = async (ruleId: number) => {
        try {
            const res = await patchRuleStatus(ruleId, { isActive: true });
            if (res?.success) {
                const siteId = rules.find(r => r.siteRuleId === ruleId)?.siteId;
                if (siteId) {
                    globalThis.dispatchEvent(new CustomEvent('rules:status-changed', { detail: { siteId } }));
                }
                setSuccessMessage('Rule activated successfully');
                setRules(prev => prev.map(r => 
                    r.siteRuleId === ruleId ? { ...r, isActive: true } : r
                ));
            } else {
                setError(res?.error?.message || 'Failed to activate rule');
            }
        } catch (err: any) {
            setError(err?.message || 'Failed to activate rule');
        }
    };

    const handleDeactivateRule = async (ruleId: number) => {
        try {
            const res = await patchRuleStatus(ruleId, { isActive: false });
            if (res?.success) {
                const siteId = rules.find(r => r.siteRuleId === ruleId)?.siteId;
                if (siteId) {
                    globalThis.dispatchEvent(new CustomEvent('rules:status-changed', { detail: { siteId } }));
                }
                setSuccessMessage('Rule deactivated successfully');
                setRules(prev => prev.map(r => 
                    r.siteRuleId === ruleId ? { ...r, isActive: false } : r
                ));
            } else {
                setError(res?.error?.message || 'Failed to deactivate rule');
            }
        } catch (err: any) {
            setError(err?.message || 'Failed to deactivate rule');
        }
    };

    const waitForModalConfirm = (): Promise<boolean> => {
        return new Promise(resolve => {
            resolverRef.current = resolve;
            setOpenModalConfirm(true);
        });
    };


    const handleDeleteRules = async (ruleIds: number[]) => {
        const count = ruleIds.length;
        setModalMessageText(`Are you sure you want to delete this rule${count > 1 ? ` from ${count} sites` : ''}?`);
        setModalHeadingText('Delete Rule');
        setPendingDeleteRuleIds(ruleIds);
        setOpenModalConfirm(true);

        const confirmed = await waitForModalConfirm();

        if (!confirmed) {
            setPendingDeleteRuleIds([]);
            return;
        }

        try {
            for (const id of ruleIds) {
                try {
                    const res = await deleteRule(id);
                    if (res?.success) {
                        const siteId = rules.find(r => r.siteRuleId === id)?.siteId;
                        if (siteId) {
                            globalThis.dispatchEvent(new CustomEvent('rules:deleted', { detail: { siteId } }));
                        }
                        setRules(prev => prev.filter(r => r.siteRuleId !== id));
                    }
                } catch (err) {
                    console.info('Failed to delete rule:', err);
                }
            }
            setSuccessMessage(`Successfully deleted ${count} rule(s)`);
        } catch (err: any) {
            setError(err?.message || 'Failed to delete rules');
        } finally {
            setPendingDeleteRuleIds([]);
        }
    };

    const handleNavigateToSite = (siteId: number | undefined) => {
        if (siteId) {
            navigate(`/sites/${siteId}`);
        }
    };

    const handleEditRule = async (ruleId: number, siteId: number | undefined, allRuleIds: number[] = [], allSiteIds: number[] = []) => {
        if (!siteId) {
            return;
        }
        
        setEditingRuleId(ruleId);
        setEditingSiteId(siteId);
        setEditingRuleIds(allRuleIds.length > 0 ? allRuleIds : [ruleId]); // Store all rule IDs to update
        setEditingSiteIds(allSiteIds.length > 0 ? allSiteIds : [siteId]); // Store all site IDs affected
        setSuccessMessage(null);
        setError(null);
            
        try {
            const res = await getRule(ruleId);
            if (res?.success && res.data) {
                const rule = res.data;
                // Build comma-separated list of all site IDs this rule applies to
                const siteIdsStr = (allSiteIds.length > 0 ? allSiteIds : [siteId]).join(',');
                
                const ruleFormData: RuleFormData = {
                    ruleName: rule.ruleName || '',
                    isActive: rule.isActive ?? true,
                    isUltraCritical: (rule as any).isUltraCritical ?? (rule as any).isUltraCriticalAlarm ?? false,
                    bindPathPriority: rule.bindPathPriority ?? true,
                    distributionGroupIds: (rule.distributionGroups || []).map(dg => String(dg.distributionGroupId)).join(','),
                    attachmentGroupIds: (rule.attachmentGroups || []).map(ag => String(ag.attachmentGroupId)).join(','),
                    appliesToSiteIds: siteIdsStr, // Show all currently selected sites
                    appliesToVirtualTenantIds: '',
                    filteringPaths: (rule.alarmPaths || []).map(p => String(p.id)).join(','),
                    filteringPathLabelsById: extractRuleAlarmPathLabelsById(rule.alarmPaths),
                    filteringPriorities: rule.priorities || [],
                    hideRtn: rule.hideRtn ?? false,
                    rtnExceptions: (rule.rtnExceptions || []).join(',')
                };

                try {
                    const selectedSiteIdsForEdit = (allSiteIds.length > 0 ? allSiteIds : [siteId])
                        .map(Number)
                        .filter(id => !Number.isNaN(id) && id > 0);
                    const ruleIdsToMatch = new Set(
                        (allRuleIds.length > 0 ? allRuleIds : [ruleId])
                            .map(Number)
                            .filter(id => !Number.isNaN(id) && id > 0)
                    );

                    // Primary: listSiteRules returns virtualTenants[] each with their rules[].
                    const resolvedVtIds: string[] = [];
                    for (const selectedSiteId of selectedSiteIdsForEdit) {
                        let siteHierarchyIdFromRules: number | undefined;
                        const matchedVtContainers: Array<{ vtIdCandidates: number[]; name?: string }> = [];

                        try {
                            const siteRulesRes = await listSiteRules(selectedSiteId);
                            if (siteRulesRes?.success) {
                                siteHierarchyIdFromRules = siteRulesRes.data?.siteHierarchyId
                                    ? Number(siteRulesRes.data.siteHierarchyId)
                                    : undefined;
                            }

                            if (siteRulesRes?.success && Array.isArray(siteRulesRes.data?.virtualTenants)) {
                                siteRulesRes.data.virtualTenants.forEach((vtContainer: any) => {
                                    const hasRule = Array.isArray(vtContainer.rules)
                                        && vtContainer.rules.some((vtRule: any) => ruleIdsToMatch.has(Number(vtRule.siteRuleId)));
                                    if (hasRule) {
                                        const vtIdCandidates = [
                                            vtContainer.id,
                                            vtContainer.virtualTenantId,
                                            vtContainer.siteHierarchyId,
                                            vtContainer.siteIdentifierRegistryId,
                                        ]
                                            .map((id: unknown) => Number(id))
                                            .filter((id: number) => !Number.isNaN(id) && id > 0);

                                        matchedVtContainers.push({
                                            vtIdCandidates,
                                            name: vtContainer.name ? String(vtContainer.name) : undefined,
                                        });
                                    }
                                });
                            }
                        } catch {
                            // ignore per-site errors
                        }

                        if (matchedVtContainers.length > 0) {
                            const lookupIds = [siteHierarchyIdFromRules, selectedSiteId]
                                .map(id => Number(id))
                                .filter(id => !Number.isNaN(id) && id > 0)
                                .filter((id, index, arr) => arr.indexOf(id) === index);

                            let bySiteVts: any[] = [];
                            for (const lookupId of lookupIds) {
                                try {
                                    const vtRes = await getVirtualTenantsBySite(lookupId);
                                    if (vtRes?.success && Array.isArray(vtRes.data) && vtRes.data.length > 0) {
                                        bySiteVts = vtRes.data;
                                        break;
                                    }
                                } catch {
                                    // ignore and try next lookup id
                                }
                            }

                            if (bySiteVts.length > 0) {
                                matchedVtContainers.forEach(container => {
                                    const byId = container.vtIdCandidates.length > 0
                                        ? bySiteVts.find(vt => container.vtIdCandidates.includes(Number(vt.id)))
                                        : undefined;

                                    const normalizedContainerName = (container.name || '').trim().toLowerCase();
                                    const byName = !byId && normalizedContainerName
                                        ? bySiteVts.find(vt => {
                                            const candidate = String(vt.name || vt.virtualTenantIdentifier || '').trim().toLowerCase();
                                            return candidate === normalizedContainerName;
                                        })
                                        : undefined;

                                    const resolved = byId || byName;
                                    if (resolved?.id) {
                                        resolvedVtIds.push(String(resolved.id));
                                    } else if (container.vtIdCandidates.length > 0) {
                                        // Keep old behavior as a final fallback for environments where ids already align.
                                        resolvedVtIds.push(String(container.vtIdCandidates[0]));
                                    }
                                });
                            } else {
                                matchedVtContainers.forEach(container => {
                                    if (container.vtIdCandidates.length > 0) {
                                        resolvedVtIds.push(String(container.vtIdCandidates[0]));
                                    }
                                });
                            }
                        }
                    }

                    // Fallback: if listSiteRules virtualTenants didn't yield a match,
                    // use by-site endpoint + VT alarm-paths endpoint and match by alarm path subset.
                    if (resolvedVtIds.length === 0) {
                        const rulePathIdSet = new Set(
                            (rule.alarmPaths || [])
                                .map(p => Number(p.id))
                                .filter(id => !Number.isNaN(id) && id > 0)
                        );

                        if (rulePathIdSet.size > 0) {
                            const vtPathCache = new Map<string, Set<number>>();

                            for (const selectedSiteId of selectedSiteIdsForEdit) {
                                let hierarchyId: number | undefined;
                                try {
                                    const siteRulesRes = await listSiteRules(selectedSiteId);
                                    if (siteRulesRes?.success && siteRulesRes.data?.siteHierarchyId) {
                                        hierarchyId = Number(siteRulesRes.data.siteHierarchyId);
                                    }
                                } catch {
                                    // ignore
                                }

                                const primaryId = hierarchyId ?? selectedSiteId;
                                try {
                                    const vtRes = await getVirtualTenantsBySite(primaryId);
                                    if (vtRes?.success && Array.isArray(vtRes.data) && vtRes.data.length > 0) {
                                        for (const vt of vtRes.data) {
                                            const vtId = String(vt.id);
                                            if (!vtId) {
                                                continue;
                                            }

                                            if (!vtPathCache.has(vtId)) {
                                                let vtPathSet = new Set<number>();

                                                try {
                                                    const pathsRes = await getVirtualTenantAlarmPaths(vtId);
                                                    if (pathsRes?.success && Array.isArray(pathsRes.data)) {
                                                        const ids = extractAlarmPathIdsFromVirtualTenantPayload(pathsRes.data);
                                                        vtPathSet = new Set(ids);
                                                    }
                                                } catch {
                                                    // ignore per-vt path lookup errors
                                                }
                                                if (vtPathSet.size === 0 && Array.isArray(vt.associatedAlarmPaths)) {
                                                    vtPathSet = new Set(
                                                        vt.associatedAlarmPaths
                                                            .map(p => Number(p))
                                                            .filter(n => !Number.isNaN(n) && n > 0)
                                                    );
                                                }

                                                vtPathCache.set(vtId, vtPathSet);
                                            }

                                            const cachedVtPathSet = vtPathCache.get(vtId);
                                            if (!cachedVtPathSet || cachedVtPathSet.size === 0) {
                                                continue;
                                            }

                                            let allPresent = true;
                                            for (const rulePathId of rulePathIdSet) {
                                                if (!cachedVtPathSet.has(rulePathId)) {
                                                    allPresent = false;
                                                    break;
                                                }
                                            }

                                            if (allPresent) {
                                                resolvedVtIds.push(vtId);
                                            }
                                        }
                                    }
                                } catch {
                                    // ignore per-site errors
                                }
                            }
                        }
                    }

                    if (resolvedVtIds.length === 0) {
                        ruleIdsToMatch.forEach(candidateRuleId => {
                            const mapped = ruleToVirtualTenantMapping.get(candidateRuleId) || [];
                            mapped.forEach(vtId => {
                                if (vtId) {
                                    resolvedVtIds.push(String(vtId));
                                }
                            });
                        });
                    }

                    if (resolvedVtIds.length === 0) {
                        try {
                            const raw = localStorage.getItem('rules:ruleToVirtualTenantMapping');
                            if (raw) {
                                const arr = JSON.parse(raw) as Array<[number, string[]]>;
                                const persistedMap = new Map<number, string[]>(
                                    arr.map(([k, v]) => [Number(k), Array.isArray(v) ? v.map(String) : []])
                                );
                                ruleIdsToMatch.forEach(candidateRuleId => {
                                    const mapped = persistedMap.get(candidateRuleId) || [];
                                    mapped.forEach(vtId => {
                                        if (vtId) {
                                            resolvedVtIds.push(String(vtId));
                                        }
                                    });
                                });
                            }
                        } catch {
                            // ignore persisted-mapping parse errors
                        }
                    }

                    if (resolvedVtIds.length > 0) {
                        ruleFormData.appliesToVirtualTenantIds = [...new Set(resolvedVtIds)].join(',');
                    }
                } catch {
                    // No VT prefill could be inferred from backend.
                }
                setEditingRuleData(ruleFormData);
                setIsEditModalOpen(true);
            } else {
                setError(res?.error?.message || 'Failed to load rule details');
            }
        } catch (err: any) {
            setError(err?.message || String(err));
        }
    };

    const handleOpenCreateModal = () => {
        setNewRulesData([{
            ruleName: '',
            isActive: true,
            isUltraCritical: false,
            bindPathPriority: false,
            distributionGroupIds: '',
            attachmentGroupIds: '',
            appliesToSiteIds: '',
            appliesToVirtualTenantIds: '',
            filteringPaths: '',
            filteringPriorities: [],
            hideRtn: false,
            rtnExceptions: ''
        }]);
        setIsCreateModalOpen(true);
        setSuccessMessage(null);
        setError(null);
    };

    const handleCreateRules = async () => {
        if (!isCreateFormValid) {
            setShowCreateErrorSummary(true);
            return;
        }
        
        setSuccessMessage(null);
        setError(null);
        setShowCreateErrorSummary(false);
        
        try {
            // Create rules for each selected site
            const siteIds = newRulesData[0]?.appliesToSiteIds?.split(',').filter(Boolean) || [];
            const isMultiSiteCreate = siteIds.length > 1;
            const selectedVtIds = newRulesData[0]?.appliesToVirtualTenantIds?.split(',').map(s => s.trim()).filter(Boolean) || [];
            const selectedPathIds = parseFilteringPaths(newRulesData[0]?.filteringPaths);
            
            if (siteIds.length === 0) {
                setError('Please select at least one site');
                return;
            }
            
            const createPromises = siteIds.map(async siteId => {
                let siteData = sitesMap.get(Number(siteId));

                if (!siteData) {
                    try {
                        const siteRes = await getSite(Number(siteId));
                        if (siteRes?.success && siteRes.data) {
                            siteData = {
                                id: Number(siteId),
                                siteName: siteRes.data.name,
                                siteIdentifier: siteRes.data.siteIdentifier,
                                customerName: siteRes.data.customerName,
                                customerId: siteRes.data.customerId
                            } as SiteListItem;
                            // cache locally for subsequent uses
                            setSitesMap(prev => {
                                const copy = new Map(prev);
                                copy.set(Number(siteId), siteData as SiteListItem);
                                return copy;
                            });
                        } else {
                            return { success: false, error: { message: `Site ${siteId} not found` } };
                        }
                    } catch (err) {
                        return { success: false, error: { message: `Failed to resolve site ${siteId}` } };
                    }
                }

                // Query backend to get the correct siteHierarchyId for this site
                let hierarchyId: number | undefined;
                let vtHierarchyId: number | undefined;
                try {
                    const rulesRes = await listSiteRules(Number(siteId));
                    if (rulesRes?.success && rulesRes.data?.siteHierarchyId) {
                        hierarchyId = rulesRes.data.siteHierarchyId;
                        const vtContainer = (rulesRes.data.virtualTenants || [])
                            .find(v => selectedVtIds.includes(String(v.siteHierarchyId)));
                        if (vtContainer) {
                            vtHierarchyId = Number(vtContainer.siteHierarchyId);
                        }
                    } else {
                        return { success: false, error: { message: `Cannot determine hierarchy ID for site ${siteId}. The site may not exist in the rules system yet.` } };
                    }
                } catch (err) {
                    return { success: false, error: { message: `Failed to query site ${siteId}: ${err}` } };
                }

                const ruleData = newRulesData[0];
                let rulePathIds = parseFilteringPaths(ruleData.filteringPaths);
                // A VT rule without paths would match every alarm on the parent site.
                if (vtHierarchyId && rulePathIds.length === 0) {
                    const vtPathsRes = await getVirtualTenantAlarmPaths(vtHierarchyId);
                    if (vtPathsRes?.success && Array.isArray(vtPathsRes.data)) {
                        rulePathIds = extractAlarmPathIdsFromVirtualTenantPayload(vtPathsRes.data);
                    }
                    if (rulePathIds.length === 0) {
                        return { success: false, error: { message: 'Selected virtual tenant has no alarm paths. Select paths from the parent site.' } };
                    }
                }
                const baseRuleName = (ruleData.ruleName || 'New Rule').trim();
                const siteDisplayName = (siteData?.siteName || siteData?.siteIdentifier || `Site ${siteId}`).trim();
                const finalRuleName = isMultiSiteCreate ? `${baseRuleName} - ${siteDisplayName}` : baseRuleName;
                const payload: any = {
                    siteIdentifierRegistryId: Number(siteId),
                    siteHierarchyId: vtHierarchyId ?? hierarchyId,
                    entityType: vtHierarchyId ? 'VirtualTenant' : 'Site',
                    ruleName: finalRuleName,
                    isActive: !!ruleData.isActive,
                    bindPathPriority: !!ruleData.bindPathPriority,
                    filteringCriteria: {
                        pathIds: rulePathIds,
                        paths: rulePathIds.map(n => String(n)),
                        priorities: Array.isArray(ruleData.filteringPriorities) ? ruleData.filteringPriorities : [],
                        classes: [],
                        patterns: []
                    },
                    distributionGroupIds: (ruleData.distributionGroupIds || '').split(',').map((s: string) => Number(s.trim())).filter(Boolean),
                    attachmentGroupIds: (ruleData.attachmentGroupIds || '').split(',').map((s: string) => Number(s.trim())).filter(Boolean),
                    behavior: {
                        hideRtn: !!ruleData.hideRtn,
                        rtnExceptions: (ruleData.rtnExceptions || '')
                            .split(',')
                            .map((s: string) => s.trim())
                            .filter((s: string) => s !== '' && !Number.isNaN(Number(s)) && Number(s) > 0)
                    },
                    isUltraCritical:newRulesData[0]?.isUltraCritical
                };
                
                const response = await createRule(payload);
                return response;
            });
            
            const results = await Promise.all(createPromises);
            const successCount = results.filter(r => r?.success).length;
            const failCount = results.length - successCount;

            let vtAssociationSuccessCount = 0;
            let vtAssociationFailCount = 0;
            if (successCount > 0 && selectedVtIds.length > 0 && selectedPathIds.length > 0) {
                const vtAssociationResults = await Promise.allSettled(
                    selectedVtIds.map(vtId => addVirtualTenantAlarmPaths(vtId, { alarmPathIds: selectedPathIds }))
                );
                vtAssociationResults.forEach(result => {
                    if (result.status === 'fulfilled' && result.value?.success) {
                        vtAssociationSuccessCount++;
                    } else {
                        vtAssociationFailCount++;
                    }
                });
            }
            
            if (successCount > 0) {
                // Track the correct site ID for each newly created rule
                // This is needed because backend returns wrong siteHierarchyId in rules_list
                const newMappings = new Map(ruleToSiteMapping);
                
                results.forEach((result, idx) => {
                    if (result?.success && 'data' in result && result.data?.siteRuleId) {
                        const ruleId = result.data.siteRuleId;
                        const correctSiteId = Number(siteIds[idx]);
                        newMappings.set(ruleId, correctSiteId);
                    }
                });
                
                setRuleToSiteMapping(newMappings);

                if (selectedVtIds.length > 0) {
                    setRuleToVirtualTenantMapping(prev => {
                        const next = new Map(prev);
                        results.forEach(result => {
                            if (result?.success && 'data' in result && result.data?.siteRuleId) {
                                next.set(Number(result.data.siteRuleId), selectedVtIds.map(String));
                            }
                        });
                        return next;
                    });
                }
                
                let successText = `Successfully created ${successCount} rule(s)${failCount > 0 ? ` (${failCount} failed)` : ''}`;
                if (selectedVtIds.length > 0 && selectedPathIds.length > 0) {
                    successText += `. VT path updates: ${vtAssociationSuccessCount} applied`;
                    if (vtAssociationFailCount > 0) {
                        successText += `, ${vtAssociationFailCount} failed`;
                    }
                }
                setSuccessMessage(successText);
                setIsCreateModalOpen(false);
                setNewRulesData([]);
                
                // Refresh the rules list - fetchRules will use ruleToSiteMapping to correct the data
                await fetchRules();
                // Notify other parts of the app (e.g., SiteDetails) that rules were created for these sites
                try {
                    results.forEach((r, idx) => {
                        if (r && r.success) {
                            const sid = Number(siteIds[idx]);
                            try {
                                window.dispatchEvent(new CustomEvent('rules:created', { detail: { siteId: sid } }));
                            } catch (e) {
                                console.info('[Rules] Could not dispatch rules:created event', e);
                            }
                        }
                    });
                } catch (e) {
                    console.info('[Rules] Error emitting rules:created events', e);
                }
            } else {
                setError('Failed to create rules');
            }
        } catch (err: any) {
            setError(err?.message || 'Failed to create rules');
        }
    };

    const handleUpdateRule = async (ruleId: number, ruleData: RuleFormData) => {
        if (!isEditFormValid) {
            setShowEditErrorSummary(true);
            return;
        }
        
        setSuccessMessage(null);
        setError(null);
        setShowEditErrorSummary(false);
        
        try {
            // Parse new site IDs from the form
            const newSiteIds = (ruleData.appliesToSiteIds || '')
                .split(',')
                .map(s => Number(s.trim()))
                .filter(Boolean);
            
            const oldSiteIds = editingSiteIds.length > 0 ? editingSiteIds : [editingSiteId].filter(Boolean) as number[];
            const oldRuleIds = editingRuleIds.length > 0 ? editingRuleIds : [ruleId];
            
            // Determine which sites were added, removed, or remain
            const sitesToAdd = newSiteIds.filter(id => !oldSiteIds.includes(id));
            const sitesToRemove = oldSiteIds.filter(id => !newSiteIds.includes(id));
            const sitesToUpdate = oldSiteIds.filter(id => newSiteIds.includes(id));
            
            const payload: any = {
                ruleName: ruleData.ruleName || `Rule ${ruleId}`,
                isActive: !!ruleData.isActive,
                isUltraCritical: !!ruleData.isUltraCritical,
                bindPathPriority: !!ruleData.bindPathPriority,
                filteringCriteria: {
                    pathIds: parseFilteringPaths(ruleData.filteringPaths),
                    paths: parseFilteringPaths(ruleData.filteringPaths).map(n => String(n)),
                    priorities: Array.isArray(ruleData.filteringPriorities) ? ruleData.filteringPriorities : [],
                    classes: [],
                    patterns: []
                },
                distributionGroupIds: (ruleData.distributionGroupIds || '').split(',').map((s: string) => Number(s.trim())).filter(Boolean),
                attachmentGroupIds: (ruleData.attachmentGroupIds || '').split(',').map((s: string) => Number(s.trim())).filter(Boolean),
                behavior: {
                    hideRtn: !!ruleData.hideRtn,
                    rtnExceptions: (ruleData.rtnExceptions || '')
                        .split(',')
                        .map((s: string) => s.trim())
                        .filter((s: string) => s !== '' && !Number.isNaN(Number(s)) && Number(s) > 0)
                }
            };
            
            console.info('[Rules] Update payload:', payload);
            
            let successCount = 0;
            let failCount = 0;
            const updatedRuleIds: number[] = [];
            const affectedSiteIds: number[] = [];
            
            // 1. Update existing rules for sites that remain
            for (let i = 0; i < sitesToUpdate.length; i++) {
                const siteId = sitesToUpdate[i];
                const ruleIdToUpdate = oldRuleIds[oldSiteIds.indexOf(siteId)];
                if (ruleIdToUpdate) {
                    try {
                        const res = await updateRule(ruleIdToUpdate, payload);
                        if (res?.success) {
                            successCount++;
                            updatedRuleIds.push(ruleIdToUpdate);
                            affectedSiteIds.push(siteId);
                        } else {
                            failCount++;
                        }
                    } catch (err) {
                        failCount++;
                    }
                }
            }
            
            // 2. Delete rules for removed sites
            for (let i = 0; i < sitesToRemove.length; i++) {
                const siteId = sitesToRemove[i];
                const ruleIdToDelete = oldRuleIds[oldSiteIds.indexOf(siteId)];
                if (ruleIdToDelete) {
                    try {
                        const res = await deleteRule(ruleIdToDelete);
                        if (res?.success) {
                            successCount++;
                            // Remove from local state
                            setRules(prev => prev.filter(r => r.siteRuleId !== ruleIdToDelete));
                            setRuleToVirtualTenantMapping(prev => {
                                const next = new Map(prev);
                                next.delete(ruleIdToDelete);
                                return next;
                            });
                        } else {
                            failCount++;
                        }
                    } catch (err) {
                        failCount++;
                    }
                }
            }
            
            // 3. Create new rules for added sites
            for (const siteId of sitesToAdd) {
                try {
                    console.info(`[Rules] Creating new rule for added site ${siteId}`);
                    
                    // Get site details to get hierarchyId
                    const siteRes = await listSiteRules(siteId as any);
                    const hierarchyId = siteRes?.data?.siteHierarchyId;
                    
                    if (!hierarchyId) {
                        failCount++;
                        continue;
                    }
                    
                    const createPayload = {
                        siteIdentifierRegistryId: siteId,
                        siteHierarchyId: hierarchyId,
                        entityType: 'Site',
                        ...payload
                    };
                    
                    const res = await createRule(createPayload);
                    if (res?.success && res.data?.siteRuleId) {
                        successCount++;
                        updatedRuleIds.push(res.data.siteRuleId);
                        affectedSiteIds.push(siteId);
                        
                        // Track the new rule in localStorage
                        try {
                            const newMappings = new Map(ruleToSiteMapping);
                            newMappings.set(res.data.siteRuleId, siteId);
                            setRuleToSiteMapping(newMappings);
                        } catch (e) {
                            console.info('[Rules] Failed to update ruleToSiteMapping', e);
                        }
                    } else {
                        failCount++;
                        console.info(`[Rules] Failed to create rule for site ${siteId}:`, res?.error);
                    }
                } catch (err) {
                    failCount++;
                    console.info(`[Rules] Error creating rule for site ${siteId}:`, err);
                }
            }
            
            if (successCount > 0) {
                const selectedVtIds = (ruleData.appliesToVirtualTenantIds || '')
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean);

                setRuleToVirtualTenantMapping(prev => {
                    const next = new Map(prev);
                    updatedRuleIds.forEach(updatedId => {
                        if (selectedVtIds.length > 0) {
                            next.set(updatedId, selectedVtIds);
                        } else {
                            next.delete(updatedId);
                        }
                    });
                    return next;
                });

                // Find the rule to get customer name for the success message
                const currentRule = rules.find(r => r.siteRuleId === ruleId);
                const customerName = currentRule?.customerName || 'Customer';
                const ruleName = ruleData.ruleName || 'Rule';
                
                // Build success message based on what operations were performed
                let successMsg = `${customerName} - ${ruleName}`;
                const operations: string[] = [];
                if (sitesToUpdate.length > 0) {operations.push(`updated ${sitesToUpdate.length} site(s)`);}
                if (sitesToAdd.length > 0) {operations.push(`added to ${sitesToAdd.length} site(s)`);}
                if (sitesToRemove.length > 0) {operations.push(`removed from ${sitesToRemove.length} site(s)`);}
                if (operations.length > 0) {
                    successMsg += `: ${operations.join(', ')}`;
                }
                setSuccessMessage(successMsg);
                
                // Close modal first
                setEditingRuleId(null);
                setEditingRuleData(null);
                setEditingSiteId(null);
                setEditingRuleIds([]);
                setEditingSiteIds([]);
                setIsEditModalOpen(false);
                
                // Add a small delay to allow server to process, then refetch ALL updated rules
                setTimeout(async () => {
                    try {
                     
                        // Refresh the entire rules list to get all changes
                        await fetchRules();
                        
                        // Note: fetchRules() already updated the rules list, so we don't need to manually update state here
                        
                        // Emit rules:updated/created events for ALL affected sites
                        try {
                            affectedSiteIds.forEach(siteId => {
                                if (siteId) {
                                    globalThis.dispatchEvent(new CustomEvent('rules:updated', { 
                                        detail: { siteId } 
                                    }));
                                }
                            });
                            // Also emit for removed sites so they can refresh and remove the rule
                            sitesToRemove.forEach(siteId => {
                                window.dispatchEvent(new CustomEvent('rules:updated', { 
                                    detail: { siteId } 
                                }));
                            });
                        } catch (e) {
                            console.info('[Rules] Could not dispatch events', e);
                        }
                    } catch (err) {
                        console.info('[Rules] Failed to refetch updated rules:', err);
                    }
                }, 500);
            } else {
                setError(`Failed to update rule${failCount > 1 ? 's' : ''}`);
            }
        } catch (err: any) {
            setError(err?.message || String(err));
        }
    };

    // Filter and sort rules
    // Prefer backend entityType; legacy VT rules were saved as 'Site' and are only known via the local VT mapping.
    const isVirtualTenantRule = (rule: RuleWithContext): boolean => {
        const rawType = rule.entityType ?? (rule as { entity_type?: string }).entity_type;
        if (rawType) {
            return String(rawType).toLowerCase() === 'virtualtenant';
        }
        return (ruleToVirtualTenantMapping.get(rule.siteRuleId)?.length ?? 0) > 0
            || (ruleToVirtualTenantNames.get(rule.siteRuleId)?.length ?? 0) > 0;
    };

    const filteredRules = useMemo(() => {
        let result = [...rules];

        // Search filter
        if (searchQuery && searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter(rule => {
                const searchableFields = [
                    rule.ruleName,
                    rule.siteName,
                    rule.siteIdentifier,
                    rule.customerName,
                    rule.isActive ? 'active' : 'inactive',
                ];
                return searchableFields.some(field => 
                    field && String(field).toLowerCase().includes(query)
                );
            });
        }

        // Status radio filter (active/inactive/all)
        if (statusFilter === 'active') {
            result = result.filter(r => !!r.isActive);
        } else if (statusFilter === 'inactive') {
            result = result.filter(r => !r.isActive);
        }
        else {
            // 'all' - no filtering
        }

        if (entityTypeFilter !== 'all') {
            const wantVirtualTenant = entityTypeFilter === 'virtualTenant';
            result = result.filter(r => isVirtualTenantRule(r) === wantVirtualTenant);
        }

        // Sort
        if (sortColumn) {
            result.sort((a, b) => {
                let aVal: any;
                let bVal: any;

                switch (sortColumn) {
                    case 'ruleName':
                        aVal = a.ruleName || '';
                        bVal = b.ruleName || '';
                        break;
                    case 'siteName':
                        aVal = getRuleSiteOrVirtualTenantLabel(a);
                        bVal = getRuleSiteOrVirtualTenantLabel(b);
                        break;
                    case 'customerName':
                        aVal = a.customerName || '';
                        bVal = b.customerName || '';
                        break;
                    case 'status':
                        aVal = a.isActive ? 'Active' : 'Inactive';
                        bVal = b.isActive ? 'Active' : 'Inactive';
                        break;
                    case 'distributionGroups':
                        aVal = a.distributionGroupCount || 0;
                        bVal = b.distributionGroupCount || 0;
                        break;
                    case 'attachmentGroups':
                        aVal = a.attachmentGroupCount || 0;
                        bVal = b.attachmentGroupCount || 0;
                        break;
                    case 'createdAt':
                        aVal = a.createdAt || '';
                        bVal = b.createdAt || '';
                        break;
                    default:
                        return 0;
                }

                if (aVal < bVal) {
                    return sortOrder === 'asc' ? -1 : 1;
                }
                if (aVal > bVal) {
                    return sortOrder === 'asc' ? 1 : -1;
                }
                return 0;
            });
        }

        return result;
    }, [rules, searchQuery, sortColumn, sortOrder, statusFilter, entityTypeFilter, ruleToVirtualTenantMapping, virtualTenantsMap, ruleToVirtualTenantNames]);

    // One row per rule for instances
    const groupedRules = useMemo(() => {
        const vtIdByNormalizedName = new Map<string, number>();
        virtualTenantsMap.forEach((name, id) => {
            const normalized = String(name || '').trim().toLowerCase();
            if (normalized && !vtIdByNormalizedName.has(normalized)) {
                vtIdByNormalizedName.set(normalized, id);
            }
        });

        return filteredRules.map(rule => {
            const mappedVtIds = getVirtualTenantIdsForRule(rule);

            const inferredVtNames = (ruleToVirtualTenantNames.get(rule.siteRuleId) || [])
                .map(name => String(name || '').trim())
                .filter(Boolean)
                .filter((name, index, arr) => arr.indexOf(name) === index);

            const appliedSites = rule.siteId
                ? [{
                    id: rule.siteId,
                    name: rule.siteName || rule.siteIdentifier || `Site ${rule.siteId}`,
                    identifier: rule.siteIdentifier
                }]
                : [];

            let appliedTargets: Array<{ id: number; name: string; identifier?: string; isVirtualTenant: boolean; canLink?: boolean }>;

            if (mappedVtIds.length > 0) {
                appliedTargets = mappedVtIds.map(vtId => ({
                    id: vtId,
                    name: virtualTenantsMap.get(vtId) || `VT ${vtId}`,
                    isVirtualTenant: true,
                    canLink: true
                }));
            } else if (inferredVtNames.length > 0) {
                appliedTargets = inferredVtNames.map(vtName => {
                    const resolvedVtId = vtIdByNormalizedName.get(vtName.toLowerCase());
                    return {
                        id: resolvedVtId || 0,
                        name: vtName,
                        isVirtualTenant: true,
                        canLink: !!resolvedVtId
                    };
                });
            } else if (/(^|\s)vt(\s|$)/i.test(String(rule.ruleName || ''))) {
                const parentCandidates = [rule.siteId, rule.siteHierarchyId]
                    .map(id => Number(id))
                    .filter(id => !Number.isNaN(id) && id > 0);

                let fallbackTargets: Array<{ id: number; name: string; identifier?: string; isVirtualTenant: boolean; canLink?: boolean }> = [];
                for (const parentId of parentCandidates) {
                    const vtList = virtualTenantsByParentSite.get(parentId) || [];
                    if (vtList.length > 0) {
                        fallbackTargets = vtList.map(vt => ({
                            id: vt.id,
                            name: vt.name,
                            isVirtualTenant: true,
                            canLink: true
                        }));
                        break;
                    }
                }

                if (fallbackTargets.length === 0) {
                    const normalizedSiteName = String(rule.siteName || '').trim().toLowerCase();
                    if (normalizedSiteName) {
                        const byName = virtualTenantsByParentSiteName.get(normalizedSiteName) || [];
                        if (byName.length > 0) {
                            fallbackTargets = byName.map(vt => ({
                                id: vt.id,
                                name: vt.name,
                                isVirtualTenant: true,
                                canLink: true
                            }));
                        }
                    }
                }

                appliedTargets = fallbackTargets.length > 0
                    ? fallbackTargets
                    : appliedSites.map(site => ({
                        id: site.id,
                        name: site.name,
                        identifier: site.identifier,
                        isVirtualTenant: false,
                        canLink: true
                    }));
            } else {
                appliedTargets = appliedSites.map(site => ({
                    id: site.id,
                    name: site.name,
                    identifier: site.identifier,
                    isVirtualTenant: false,
                    canLink: true
                }));
            }

            return {
                ...rule,
                appliedSites,
                appliedTargets,
                allRuleIds: [rule.siteRuleId],
            } as RuleWithContext & {
                appliedSites: Array<{ id: number; name: string; identifier?: string }>;
                appliedTargets: Array<{ id: number; name: string; identifier?: string; isVirtualTenant: boolean; canLink?: boolean }>;
                allRuleIds: number[];
            };
        });
    }, [filteredRules, ruleToVirtualTenantMapping, virtualTenantsMap, ruleToVirtualTenantNames, virtualTenantsByParentSite, virtualTenantsByParentSiteName]);

    // Pagination: ensure current page is within range when filters change
    useEffect(() => {
        const totalPages = Math.max(1, Math.ceil((groupedRules?.length || 0) / pageSize));
        if (currentPage > totalPages) {
            setCurrentPage(1);
        }
    }, [groupedRules, currentPage]);

    const statusTitlePrefix = statusFilter === 'all' ? 'All' : (statusFilter === 'active' ? 'Active' : 'Inactive');
    const entityTypeTitle = entityTypeFilter === 'site' ? 'Site Rules' : (entityTypeFilter === 'virtualTenant' ? 'Virtual Tenant Rules' : 'Rules');
    const headerTitle = `${statusTitlePrefix} ${entityTypeTitle}`;

    return (
        <main>
            <div className="container mb-2">
                <section className='page'>
                    <div className="heading__section">
                        <div className="heading__title-group">
                            <h2 className="heading__title heading__title--rules">Rules Management</h2>
                            <DateTimeDisplay />
                            <Breadcrumbs items={breadcrumbs} />
                        </div>
                        <div>
                            <button type="button" className="btn btn-add-new" onClick={handleOpenCreateModal}>Add Rule</button>
                        </div>
                    </div>
                </section>

                {successMessage && (
                    <StatusMessage kind="success" onClose={() => setSuccessMessage(null)}>
                        {successMessage}
                    </StatusMessage>
                )}

                {error && (
                    <StatusMessage kind="error" dismissible={false}>
                        {error}
                    </StatusMessage>
                )}

                <section className="alarm-content">
                    <div className="toolbar-content pt-1 pb-1">
                            <div className="toolbar-content__left">
                                <h3 className='heading__title heading__title--rules'>{headerTitle}</h3>
                                <div className="toolbar-content__search">
                                    <SearchQuery searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
                                </div>
                            </div>

                            <div className="toolbar-content__right">
                                <div className="alarm-controls">
                                    <div className="filter-container">
                                        <FilterBtn onClick={() => setIsFilterOpen(!isFilterOpen)} isActive={statusFilter !== 'all' || entityTypeFilter !== 'all'} isDropdownOpen={isFilterOpen} />
                                        <RuleStatusFilter isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} value={statusFilter} onChange={(v) => setStatusFilter(v)} entityType={entityTypeFilter} onEntityTypeChange={setEntityTypeFilter} />
                                    </div>


                            </div>
                        </div>
                    </div>
                </section>


                <section className="alarm-content mb-2 p-1 mt-2">

                    <div className="alarm-content--heading">
                           <div>
                                <h3 className="table-title__alarms-list mt-0">Rules Results ({Math.min(pageSize, groupedRules.length)} of {groupedRules.length})</h3>
                                {sortColumn && (
                                    <SortSummary
                                        columnLabel={SORT_COLUMN_LABELS[sortColumn]}
                                        sortOrder={sortOrder}
                                        onReset={handleResetSort}
                                    />
                                )}
                            </div>
                        </div>
                
                    
                    {loading && (
                        <div>
                            <LoadingSpinner message="Loading rules from all sites..." />
                        </div>
                    )}

                    {!loading && !error && groupedRules.length === 0 && (
                        <div className='messages__notfound'>
                            <p>{searchQuery ? 'No rules match your search' : 'No rules found in the system'}</p>
                        </div>
                    )}

                    {!loading && groupedRules.length > 0 && (

                        <div className="table-responsive">
                            
                            <table>
                                <thead>
                                    <tr>
                                        <th onClick={() => handleSort('ruleName')} className="th--sortable">
                                            Rule Name
                                            <Sorting sortOrder={sortColumn === 'ruleName' ? sortOrder : null} />
                                        </th>
                                        <th onClick={() => handleSort('siteName')} className="th--sortable">
                                            Site / VT
                                            <Sorting sortOrder={sortColumn === 'siteName' ? sortOrder : null} />
                                        </th>
                                        <th onClick={() => handleSort('customerName')} className="th--sortable">
                                            Customer
                                            <Sorting sortOrder={sortColumn === 'customerName' ? sortOrder : null} />
                                        </th>
                                        <th onClick={() => handleSort('status')} className="th--sortable th--center">
                                            Status
                                            <Sorting sortOrder={sortColumn === 'status' ? sortOrder : null} />
                                        </th>
                                        <th onClick={() => handleSort('distributionGroups')} className="th--sortable th--right">
                                            Distribution Groups
                                            <Sorting sortOrder={sortColumn === 'distributionGroups' ? sortOrder : null} />
                                        </th>
                                        <th onClick={() => handleSort('attachmentGroups')} className="th--sortable th--right">
                                            Attachment Groups
                                            <Sorting sortOrder={sortColumn === 'attachmentGroups' ? sortOrder : null} />
                                        </th>
                                        <th onClick={() => handleSort('createdAt')} className="th--sortable">
                                            Created
                                            <Sorting sortOrder={sortColumn === 'createdAt' ? sortOrder : null} />
                                        </th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        const start = (currentPage - 1) * pageSize;
                                        const end = start + pageSize;
                                        const paged = groupedRules.slice(start, end);
                                        return paged.map((rule, idx) => (
                                        <tr key={`${rule.siteRuleId}-${idx}`}>
                                            <td>{rule.ruleName}</td>
                                            <td>
                                                {(rule as any).appliedTargets && (rule as any).appliedTargets.length > 0 ? (
                                                    <>
                                                        {(rule as any).appliedTargets.map((target: any, targetIdx: number) => (
                                                            <React.Fragment key={`${target.isVirtualTenant ? 'vt' : 'site'}-${target.id}-${String(target.name || '').toLowerCase()}-${targetIdx}`}>
                                                                {targetIdx > 0 && ', '}
                                                                {target.canLink ? (
                                                                    <Link to={target.isVirtualTenant ? `/virtualTenants/vt/${target.id}` : `/sites/${target.id}`} className="alarm-name-link">
                                                                        {target.name}
                                                                    </Link>
                                                                ) : (
                                                                    <span>{target.name}</span>
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <span>—</span>
                                                )}
                                            </td>
                                            <td>{rule.customerName || '—'}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span className={rule.isActive ? 'badge badge--active' : 'badge badge--inactive'}>{rule.isActive ? 'Active' : 'Inactive'}</span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {rule.distributionGroupCount || 0}
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                {rule.attachmentGroupCount || 0}
                                            </td>
                                            <td>{formatDateTime(rule.createdAt)}</td>
                                            <td>
                                                <ActionList
                                                    customActions={[
                                                        ...(rule.isActive ? [{
                                                            label: 'Deactivate',
                                                            onClick: async () => {
                                                                // Deactivate all rules in this group
                                                                const allIds = (rule as any).allRuleIds || [rule.siteRuleId];
                                                                for (const id of allIds) {
                                                                    await handleDeactivateRule(id);
                                                                }
                                                            },
                                                            className: 'actions__item--deactivate'
                                                        }] : [{
                                                            label: 'Activate',
                                                            onClick: async () => {
                                                                // Activate all rules in this group
                                                                const allIds = (rule as any).allRuleIds || [rule.siteRuleId];
                                                                for (const id of allIds) {
                                                                    await handleActivateRule(id);
                                                                }
                                                            },
                                                            className: 'actions__item--acknowledge'
                                                        }]),
                                                        {
                                                            label: 'Edit',
                                                            onClick: () => {
                                                                // Get all rule IDs and site IDs in this group for multi-site editing
                                                                const allIds = (rule as any).allRuleIds || [rule.siteRuleId];
                                                                const allSiteIds = rule.appliedSites.map((s: any) => s.id);
                                                                handleEditRule(rule.siteRuleId, rule.siteId, allIds, allSiteIds);
                                                            },
                                                            className: 'actions__item--edit'
                                                        },
                                                        ...(rule.appliedSites.length === 1 ? [{
                                                            label: 'View Site',
                                                            onClick: () => handleNavigateToSite(rule.appliedSites[0].id),
                                                            className: 'actions__item--view'
                                                        }] : []),
                                                        ...(!rule.isActive ? [{
                                                            label: 'Delete',
                                                            onClick: () => {
                                                                // Delete all rules in this group
                                                                const allIds = (rule as any).allRuleIds || [rule.siteRuleId];
                                                                void handleDeleteRules(allIds);
                                                            },
                                                            className: 'actions__item--delete'
                                                        }] : [])
                                                    ]}
                                                />
                                            </td>
                                        </tr>
                                        ));
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    )}

                    
                </section>

                {/* Pagination controls */}
                {!loading && !error && groupedRules.length > pageSize && (
                    <div className="mt-2">
                        <Pagination currentPage={currentPage} totalPages={Math.max(1, Math.ceil(groupedRules.length / pageSize))} onPageChange={p => setCurrentPage(p)} />
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                <Modal
                    isOpen={openModalConfirm}
                    title={modalHeadingText ?? ''}
                    headerClass="modal__header--danger"
                    content={<ModalContent heading={modalHeadingText} message={modalMessageText} />}
                    onConfirm={() => {
                        resolverRef.current?.(true);
                        setOpenModalConfirm(false);
                    }}
                    onCancel={() => {
                        resolverRef.current?.(false);
                        setOpenModalConfirm(false);
                    }}
                />

                {/* Edit Rule Modal */}
                <Modal
                    isOpen={isEditModalOpen}
                    onCancel={() => {
                        setIsEditModalOpen(false);
                        setEditingRuleId(null);
                        setEditingRuleData(null);
                        setEditingSiteId(null);
                        setEditingRuleIds([]);
                        setEditingSiteIds([]);
                        setShowEditErrorSummary(false);
                    }}
                    onConfirm={() => {
                        if (editingRuleId && editingRuleData) {
                            return handleUpdateRule(editingRuleId, editingRuleData);
                        }
                    }}
                    title="Edit Rule"
                    content={
                        editingRuleData && editingSiteId && (
                            <>
                                <h3 className='heading__title heading__title--info mt-1 mb-1'>Rule Configuration</h3>
                                <p className="text-muted mb-1">You can add or remove sites for this rule. Changes will create/update/delete rule instances accordingly.</p>
                                <RulesForm
                                    value={[editingRuleData]}
                                    onChange={(data) => setEditingRuleData(data[0] || null)}
                                    disabled={false}
                                    siteId={editingSiteId}
                                    hideBuildingSelector={false}
                                    hideAddRuleButton={true}
                                    editIndex={0}
                                    onValidate={(isValid) => setIsEditFormValid(isValid)}
                                    showErrorSummary={showEditErrorSummary}
                                    onCloseErrorSummary={() => setShowEditErrorSummary(false)}
                                />
                            </>
                        )
                    }
                />

                {/* Create Rule Modal */}
                <Modal
                    isOpen={isCreateModalOpen}
                    onCancel={() => {
                        setIsCreateModalOpen(false);
                        setNewRulesData([]);
                        setShowCreateErrorSummary(false);
                    }}
                    onConfirm={handleCreateRules}
                    title="Create New Rule(s)"
                    content={
                        <>
                            <h3 className='heading__title heading__title--info mt-1 mb-1'>Rule Configuration</h3>
                            <p className="text-muted mb-1">Select one or more sites. The rule will be created for each selected site.</p>
                            <RulesForm
                                value={newRulesData}
                                onChange={setNewRulesData}
                                disabled={false}
                                hideBuildingSelector={false}
                                hideAddRuleButton={true}
                                onValidate={(isValid) => setIsCreateFormValid(isValid)}
                                showErrorSummary={showCreateErrorSummary}
                                onCloseErrorSummary={() => setShowCreateErrorSummary(false)}
                            />
                        </>
                    }
                />
            </div>
        </main>
    );
};

export default RulesDetails;